import {
    Alert,
    AlertDialog,
    AlertDialogContent,
    Badge,
    Button,
    Card,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    Tooltip,
} from "@jhool-io/fe-components";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import React from "react";
import {
    Link,
    useLocation,
    useParams,
    useSearchParams,
} from "react-router-dom";
import Avatar from "../../components/Avatar/Avatar";
import PinIcon from "../../components/Icons/Pin";
import UnpinIcon from "../../components/Icons/Unpin";
import ChangeCode from "../../components/Notes/NoteContent/ChangeCode/ChangeCode";

import { ViewEditorText } from "../../components/TextEditor/ViewEditorText/ViewEditorText";
import { PinnedClientsContext } from "../../context/pinned-clients/PinnedClientsContext";
import { useUpdateInvoiceStatus } from "../../hooks/mutations/billing";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../hooks/permissions";
import { useFetchDiagnosisCodes } from "../../hooks/queries";
import { useFetchSessionNote } from "../../hooks/queries/note";
import { useAppSelector } from "../../hooks/useRedux";
import useToast from "../../hooks/useToast";
import PendingResolutionForm from "../../modules/billing/components/PendingResolutionForm/PendingResolutionForm";
import { useFetchInvoices } from "../../modules/billing/hooks/queries/billing.queries";
import {
    BillStatus,
    BillStatusLabels,
} from "../../modules/billing/types/billing.types";
import ChangeInsuranceForm from "../../modules/claims/components/EditClaims/ChangeInsuranceForm/ChangeInsuranceForm";
import EditManualClaimForm from "../../modules/claims/components/EditManualClaimForm/EditManualClaimForm";
import { useUpdateClaimStatus } from "../../modules/claims/hooks/claims.mutations";
import {
    useGetClaim,
    useGetParentClaim,
} from "../../modules/claims/hooks/claims.queries";
import { ClaimStatus } from "../../modules/claims/types/claims.types";
import EditClientFlagForm from "../../modules/clients/components/EditClientFlagForm/EditClientFlagForm";
import { useDeleteClientFlag } from "../../modules/clients/hooks/clients.mutations";
import {
    useFetchClientFlags,
    useFetchClientMemos,
} from "../../modules/clients/hooks/clients.queries";
import EditNoteDiagnosisCodesForm from "../../modules/notes/components/EditNoteDiagnosisCodesForm/EditNoteDiagnosisCodesForm";
import EditNoteSessionTimeForm from "../../modules/notes/components/EditNoteSessionTimeForm/EditNoteSessionTimeForm";
import {
    getCptCodeToDisplay,
    getFlaggedNoteStatusTag,
    getNoteStatusTag,
    getNoteTypeTextWithTag,
    isPsychNote,
} from "../../modules/notes/helpers/notes.helpers";
import {
    getAdditionalCptLabel,
    getPrimaryCptLabel,
} from "../../modules/notes/helpers/noteCptCodes";
import useGetDiagnosisCodeToDisplay from "../../modules/notes/hooks";
import {
    FlaggedNoteForAuditStatus,
    ModifierTypes,
    NoteStatus,
    NoteTypes,
    Source,
} from "../../modules/notes/types/notes.types";
import useShowInsuranceOverSelfPay from "../../modules/practice-settings/hooks/useShowInsuranceOverSelfPay";
import { useFetchRemitClaims } from "../../modules/remits/hooks/remits.queries";
import { RemitStatus } from "../../modules/remits/types/remits.types";
import { UserRole } from "../../modules/user-management/types/user-management.types";
import {
    addPracticeParamToUrl,
    buildSuccessMessage,
    cn,
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    getTimeDuration,
    handleDisplayClientEmailCorrectly,
    handleRemoveEmptyParamFromQueryParams,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    userInitials,
} from "../../utils/helpers";
import { IPaginatedApiResponse } from "../../utils/types/api-response";
import { IUpdatedInvoiceResponse } from "../../utils/types/billing";
import { UserPermisions } from "../../utils/types/user";
import InfoIcon from "../../components/Icons/InfoIcon";
import { useFetchClientBasicAndPersonalInfo } from "../../hooks/queries/client";
import { FinancialAssistanceStatus } from "../../utils/types/client";
import { getClaimStatusTagStylesForDropdown } from "../../modules/claims/helpers/claims.helper";
import {
    DEFAULT_FLAG_COLOR,
    getClientFlagDetails,
} from "../../modules/clients/helpers/clients.helpers";
import ClientTagBadges from "../../modules/clients/components/ClientTagBadges/ClientTagBadges";
import EditClientTagsMenu from "../../modules/clients/components/EditClientTagsMenu/EditClientTagsMenu";
import Loader from "../Loader/Loader";
import ChevronDownIcon from "../../icons/ChevronDown";
import EditIcon from "../../icons/Edit";
import { usePendingResolutionDialogText } from "../../modules/billing/hooks/helpers.hooks";
import ChangeCodeFormPsych from "../../modules/notes/components/ChangeCodeFornPsych/ChangeCodeFormPsych";

interface HarmonizedViewProps {
    topRightAction?: React.ReactNode;
}

type Modals =
    | "change-code"
    | "pending-resolution"
    | "session-time"
    | "diagnosis-codes"
    | "edit-manual-claim"
    | "change-insurance"
    | "edit-flag"
    | "delete-flag";

export default function HarmonizedView({
    topRightAction,
}: HarmonizedViewProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [modalInView, setModalInView] = React.useState<Modals | null>(null);
    const [selectedFlagId, setSelectedFlagId] = React.useState<string | null>(
        null
    );
    const params = useParams();
    const { claimId, noteId, clientId, remitId } = params;
    const location = useLocation();
    const isGroupedClaimView = location.pathname.includes("/claims/group/");

    const viewFrom = searchParams.get("viewFrom") || "";

    const queryClient = useQueryClient();

    const activeClaimInUrlId = searchParams.get("active_claim_id");

    const pendingResolutionDialogText = usePendingResolutionDialogText();

    // Toast for success and error states
    const { toast } = useToast();

    const { practice } = useAppSelector((state) => state.userPractice);

    const showInsOverSelfPay = useShowInsuranceOverSelfPay();

    const userCanViewFlags = useDoesUserHaveRole([
        UserRole.BILLER,
        UserRole.SUPER_ADMIN,
        UserRole.BILLER_ADMIN,
        UserRole.CODER,
    ]);

    const userCanViewTags = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_TAGS_READ,
    ]);

    const userCanEditTags = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_TAGS_WRITE,
    ]);

    // Fetch client flags
    const clientFlags = useFetchClientFlags(
        clientId || "",
        userCanViewFlags && Boolean(clientId)
    );

    const deleteFlag = useDeleteClientFlag(
        clientId || "",
        selectedFlagId || ""
    );

    const isSingleNoteView = location.pathname.includes("/notes");
    const isSingleBillView = location.pathname.includes("billing-and-claims");

    const pinnedClientsContext = React.useContext(PinnedClientsContext);

    const isClientPinnedInLocation =
        pinnedClientsContext?.isClientPinnedInLocation(
            clientId || "",
            isSingleNoteView ? "note" : "bill"
        );

    const hasClaimViewPermissions = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_INVOICE_WRITE,
        UserPermisions.CLIENT_INFO_INVOICE_READ,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    // Fetch claims when user is on single claim or single remit view
    const { data: claimsData } = useGetClaim(
        {
            claim_id: activeClaimInUrlId || claimId,
        },
        Boolean((activeClaimInUrlId || claimId) && !isSingleBillView) &&
            hasClaimViewPermissions
    );

    // Fetch claims when user is on single bill or note view
    const { data: claimsDataFromSingleBillView } = useGetClaim(
        {
            note_id: noteId,
        },
        Boolean(noteId && (isSingleBillView || isSingleNoteView)) &&
            hasClaimViewPermissions
    );

    const claimWhenViewIsBillOrNote = activeClaimInUrlId
        ? claimsDataFromSingleBillView?.data?.find(
              (item) => item.claim_id === activeClaimInUrlId
          )
        : claimsDataFromSingleBillView?.data?.[0];

    const claimsInView =
        isSingleBillView || isSingleNoteView
            ? claimWhenViewIsBillOrNote
            : claimsData?.data?.[0];

    const parentClaimData = useGetParentClaim(
        claimId as string,
        Boolean(isGroupedClaimView && claimId)
    );

    const viewedClaimParentId = claimsInView?.parent_claim_id;
    const viewedClaimParent = useGetParentClaim(
        viewedClaimParentId ?? "",
        Boolean(viewedClaimParentId)
    );
    const isStatusLockedByGroup =
        (viewedClaimParent.data?.data?.child_claims?.length ?? 0) > 1;

    const mantleClaimIdToDisplay =
        claimsInView?.external_reference_id ||
        parentClaimData.data?.data?.parent_claim?.external_reference_id ||
        "--";

    const { data: remitData } = useFetchRemitClaims(
        { remittance_id: remitId },
        Boolean(remitId)
    );

    const {
        data: noteData,
        isLoading: notesLoading,
        isSuccess: noteSuccess,
    } = useFetchSessionNote(
        clientId as string,
        noteId as string,
        Boolean(clientId) && Boolean(noteId)
    );

    const noteIsPsychType = isPsychNote(noteData?.data?.type);
    const noteCptContext = noteData?.data
        ? {
              note_type: noteData.data.type,
              appointment_type: noteData.data.appointment_type,
          }
        : null;
    const primaryCptLabel = noteCptContext
        ? getPrimaryCptLabel(noteCptContext)
        : "CPT CODE";
    const additionalCptLabel = noteCptContext
        ? getAdditionalCptLabel(noteCptContext)
        : null;

    const invoiceInView = useFetchInvoices(
        {
            invoice_id: noteData?.data?.invoice_id || "",
        },
        Boolean(noteData?.data?.invoice_id)
    );

    // Fetch client basic info
    const { data: clientData } = useFetchClientBasicAndPersonalInfo(
        clientId as string,
        Boolean(clientId)
    );

    const diagnosisCodes = useFetchDiagnosisCodes();

    const clientMemos = useFetchClientMemos(clientId || "", {
        type: "user",
        memo_status: "active",
    });

    const insuranceProviderIsLoading =
        (Boolean(noteData?.data?.invoice_id) && invoiceInView.isLoading) ||
        (Boolean(clientId) && Boolean(noteId) && notesLoading);

    // check if logged in user can mark an invoice as closed
    const canMarkInvoice = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_INVOICE_WRITE,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    // check if logged in user can change modifiers code and cpt code
    const canEditCptAndModifiersCode = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_INVOICE_WRITE,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    const canEditDiagnosisCodes = useDoesUserHaveRole([
        UserRole.BILLER,
        UserRole.CODER,
        UserRole.SUPER_ADMIN,
        UserRole.AUDITOR,
    ]);

    const canEditNoteSessionTime =
        (useDoesUserHaveRole([UserRole.AUDITOR, UserRole.SUPER_ADMIN]) ||
            noteData?.data.provider.provider_id === practice?.provider_id ||
            practice?.provider_id ===
                noteData?.data?.provider?.supervisor_id) &&
        [
            NoteStatus.PENDING_SUPERVISOR_REVIEW,
            NoteStatus.CHANGES_REQUESTED,
            NoteStatus.PENDING_CODE_REVIEW,
        ].includes(noteData?.data.note_status as NoteStatus);

    const canChangeInsurance = useDoesUserHaveRole([
        UserRole.BILLER,
        UserRole.CODER,
        UserRole.SUPER_ADMIN,
        UserRole.BILLER_ADMIN,
        UserRole.CLIENT_SUPPORT,
    ]);

    /**
     * we are checking if this user can view a client personal information such as email, contact,, dob, sex e.t.c
     * we should not be showing that when the user is on the single note page
     * - first check is checking if viewFrom is not session_note and we are currently not on the single note page
     * - second check is checking if viewFrom is session_note and we are not currently on the single note page
     * (There is currently an implementation where clicking on a coded note on with a bill on the note list navigates the user to the
     *  the single bill page with the viewFrom=session_note searchparams in the url which is why this is important
     * )
     */
    const isUserViewingNoteWithoutABill =
        (viewFrom !== "session_note" && !isSingleNoteView) ||
        (viewFrom === "session_note" && !isSingleNoteView);

    // get modifier descriptions
    const getModifierCodes = () => {
        if (
            noteData?.data?.modifier_codes &&
            noteData?.data?.modifier_codes?.length > 0
        ) {
            return Object.values(ModifierTypes).filter((modifier) =>
                noteData?.data.modifier_codes
                    ?.map((code: string) => code.split(" ")[0])
                    .includes(modifier.split(" ")[0])
            );
        }

        return undefined;
    };

    const noteContent = noteData?.data.note_content;

    const secondaryDiagnosis = useGetDiagnosisCodeToDisplay(
        noteData?.data.source === Source.MANUAL
            ? noteData?.data.manual_note_diagnosis_codes?.find(
                  (code) => code.label === "secondary"
              )?.code
            : (noteContent?.secondary_diagnosis &&
                  typeof noteContent?.secondary_diagnosis === "string" &&
                  noteContent?.secondary_diagnosis) ||
                  (noteContent?.secondary_diagnosis &&
                      typeof noteContent?.secondary_diagnosis === "object" &&
                      noteContent?.secondary_diagnosis?.[0]) ||
                  ""
    );

    const tertiaryDiagnosis = useGetDiagnosisCodeToDisplay(
        noteData?.data.source === Source.MANUAL
            ? noteData?.data.manual_note_diagnosis_codes?.find(
                  (code) => code.label === "tertiary"
              )?.code
            : (noteContent?.tertiary_diagnosis &&
                  typeof noteContent?.tertiary_diagnosis === "string" &&
                  noteContent?.tertiary_diagnosis) ||
                  (noteContent?.tertiary_diagnosis &&
                      typeof noteContent?.tertiary_diagnosis === "object" &&
                      noteContent?.tertiary_diagnosis?.[0]) ||
                  ""
    );

    const additionalDiagnosisOne = useGetDiagnosisCodeToDisplay(
        noteData?.data.source === Source.MANUAL
            ? noteData?.data.manual_note_diagnosis_codes?.find(
                  (code) => code.label === "additional_one"
              )?.code
            : (noteContent?.additional_diagnosis_one &&
                  typeof noteContent?.additional_diagnosis_one === "string" &&
                  noteContent?.additional_diagnosis_one) ||
                  (noteContent?.additional_diagnosis_one &&
                      typeof noteContent?.additional_diagnosis_one ===
                          "object" &&
                      noteContent?.additional_diagnosis_one?.[0]) ||
                  ""
    );

    const additionalDiagnosisTwo = useGetDiagnosisCodeToDisplay(
        noteData?.data.source === Source.MANUAL
            ? noteData?.data.manual_note_diagnosis_codes?.find(
                  (code) => code.label === "additional_two"
              )?.code
            : (noteContent?.additional_diagnosis_two &&
                  typeof noteContent?.additional_diagnosis_two === "string" &&
                  noteContent?.additional_diagnosis_two) ||
                  (noteContent?.additional_diagnosis_two &&
                      typeof noteContent?.additional_diagnosis_two ===
                          "object" &&
                      noteContent?.additional_diagnosis_two?.[0]) ||
                  ""
    );

    const primaryDiagnosisManual = useGetDiagnosisCodeToDisplay(
        noteData?.data.source === Source.MANUAL
            ? noteData?.data.manual_note_diagnosis_codes?.find(
                  (code) => code.label === "primary"
              )?.code
            : ""
    );

    // get diagnosis descriptions
    const getDiagnosisCodes = () => {
        if (
            noteContent &&
            noteData?.data.note_content.diagnosis_codes &&
            noteData.data.note_content.diagnosis_codes.length > 0
        ) {
            return diagnosisCodes?.data?.data?.filter((code) => {
                return noteData.data.note_content.diagnosis_codes.includes(
                    code.code
                );
            });
        }

        return undefined;
    };

    const insuranceDetailsToShow = () => {
        const invoiceData = invoiceInView?.data?.data?.[0];
        const hasInvoiceId = noteData?.data.invoice_id;

        if (hasInvoiceId) {
            if (invoiceData) {
                if (invoiceData?.metadata?.self_pay && !showInsOverSelfPay) {
                    return "Self pay";
                }
                if (invoiceData?.insurance_provider) {
                    return removeEnumUnderscore(invoiceData.insurance_provider);
                }
                return "--";
            }
        }
        if (noteData?.data.client.self_pay && !showInsOverSelfPay) {
            return "Self pay";
        }
        if (noteData?.data.insurance_provider) {
            return removeEnumUnderscore(
                noteData.data.insurance_provider.toUpperCase()
            );
        }

        return "--";
    };

    // hook for updating claim status
    const patchClaimStatus = useUpdateClaimStatus();

    // hook for updating invoice status
    const patchInvoiceStatus = useUpdateInvoiceStatus();

    const handleSetModalInView = (modal: Modals | null) => {
        setModalInView(modal);
    };

    // function to change claim status
    const handleChangeStatus = (status: ClaimStatus) => {
        if (claimsInView) {
            const changeStatusPayload = {
                claim_id: claimsInView.claim_id,
                claim_status: status,
            };
            patchClaimStatus.mutate(changeStatusPayload, {
                onSuccess: (response) => {
                    queryClient.invalidateQueries([
                        "get-claim",
                        { claim_id: claimsInView.claim_id },
                    ]);

                    queryClient.invalidateQueries([
                        "get-claim",
                        { note_id: noteId },
                    ]);

                    toast({
                        mode: "success",
                        message: `${response.message}`,
                    });
                    searchParams.set("status", status);
                    setSearchParams(searchParams);
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not update claim status at this time time",
                    });
                },
            });
        }
    };

    const handleChangeBillStatus = (status: BillStatus) => {
        const changeCodePayload = {
            invoice_ids: [noteData?.data.invoice_id || ""],
            status,
        };
        patchInvoiceStatus.mutate(changeCodePayload, {
            onSuccess: (response) => {
                queryClient.setQueryData<
                    IPaginatedApiResponse<IUpdatedInvoiceResponse>
                >([clientId, `session-note`, noteId], (prev) => {
                    const prevRequired =
                        prev as IPaginatedApiResponse<IUpdatedInvoiceResponse>;
                    return {
                        ...prevRequired,
                        invoice_status: response.status,
                    };
                });
                queryClient.invalidateQueries({
                    queryKey: [clientId, `session-note`, noteId],
                });
                queryClient.invalidateQueries({
                    queryKey: [
                        "invoices",
                        handleRemoveEmptyParamFromQueryParams({
                            client_id: clientId,
                        }),
                    ],
                });
                queryClient.invalidateQueries({
                    queryKey: [
                        "invoices",
                        handleRemoveEmptyParamFromQueryParams({
                            invoice_id: noteData?.data.invoice_id,
                        }),
                    ],
                });

                toast({
                    mode: "success",
                    message: `${buildSuccessMessage(
                        response.data.updated_invoices,
                        response.data.unupdated_invoices
                    )}`,
                });
            },
            onError: () => {
                toast({
                    mode: "error",
                    message:
                        patchInvoiceStatus.error?.response?.data.message ||
                        "Could not update invoice at this time",
                });
            },
        });
    };

    // Function to delete client flag
    const handleDeleteFlag = () => {
        deleteFlag.mutate(null, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [clientId, "client-flags"],
                });
                toast({
                    mode: "success",
                    message: res.message || "Flag added successfully",
                });
                setSelectedFlagId(null);
                handleSetModalInView(null);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not add flag at this time.",
                });
            },
        });
    };

    const handleCodeModalToShow = () => {
        if (noteContent) {
            handleSetModalInView("change-code");
        } else {
            handleSetModalInView("edit-manual-claim");
        }
    };

    // Check if mutations are running, returns 0 || 1
    const changeCodeIsMutating = useIsMutating(["change-code"]);
    const updateInVoiceIsMutating = useIsMutating(["update-invoice"]);
    const editNoteIsMutating = useIsMutating(["edit-note"]);
    const editManualClaimIsMutating = useIsMutating(["edit-manual-claim"]);
    const changeInsuranceIsMutating = useIsMutating([
        "change-insurance-provider",
    ]);
    const editFlagIsMutating = useIsMutating([
        "update-client-flag",
        selectedFlagId,
    ]);

    return (
        <div className="h-[100%]">
            <Dialog open={modalInView === "diagnosis-codes"}>
                <DialogContent
                    title="Edit Diagnosis Codes"
                    variant="center"
                    handleCloseDialog={() => handleSetModalInView(null)}
                    showFooter
                    submitBtnFormValue="edit-diagnosis-codes"
                    isSubmitBtnDisabled={editNoteIsMutating > 0}
                    isCancelBtnDisabled={editNoteIsMutating > 0}
                >
                    <EditNoteDiagnosisCodesForm
                        onFormSubmit={() => handleSetModalInView(null)}
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={modalInView === "session-time"}>
                <DialogContent
                    title="Edit Session Time"
                    variant="center"
                    handleCloseDialog={() => handleSetModalInView(null)}
                    showFooter
                    submitBtnFormValue="edit-session-time"
                    isSubmitBtnDisabled={editNoteIsMutating > 0}
                    isCancelBtnDisabled={editNoteIsMutating > 0}
                >
                    <EditNoteSessionTimeForm
                        onFormSubmit={() => handleSetModalInView(null)}
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={modalInView === "change-code"}>
                <DialogContent
                    title="Change code"
                    variant="center"
                    handleCloseDialog={() => handleSetModalInView(null)}
                    showFooter
                    cancelText="Cancel"
                    saveText="Change code"
                    submitBtnFormValue="change-cpt-code"
                    isSubmitBtnDisabled={changeCodeIsMutating > 0}
                    isCancelBtnDisabled={changeCodeIsMutating > 0}
                >
                    {noteIsPsychType ? (
                        <ChangeCodeFormPsych
                            onFormSubmit={() => handleSetModalInView(null)}
                        />
                    ) : (
                        <ChangeCode
                            onFormSubmit={() => handleSetModalInView(null)}
                        />
                    )}
                </DialogContent>
            </Dialog>
            <Dialog open={modalInView === "pending-resolution"}>
                <DialogContent
                    title={pendingResolutionDialogText}
                    handleCloseDialog={() => {
                        setModalInView(null);
                        searchParams.delete("payment_failed");
                        setSearchParams(searchParams);
                    }}
                    variant="center"
                    showFooter
                    cancelText="Cancel"
                    saveText={pendingResolutionDialogText}
                    submitBtnFormValue="pending-resolution"
                    isSubmitBtnDisabled={updateInVoiceIsMutating > 0}
                    isCancelBtnDisabled={updateInVoiceIsMutating > 0}
                >
                    <PendingResolutionForm
                        onFormSubmit={() => setModalInView(null)}
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={modalInView === "edit-manual-claim"}>
                <DialogContent
                    title="Edit manual claim"
                    variant="center"
                    handleCloseDialog={() => handleSetModalInView(null)}
                    showFooter
                    cancelText="Cancel"
                    saveText="Edit claim"
                    submitBtnFormValue="edit-manual-claim"
                    isSubmitBtnDisabled={editManualClaimIsMutating > 0}
                    isCancelBtnDisabled={editManualClaimIsMutating > 0}
                >
                    <EditManualClaimForm
                        onFormSubmit={() => handleSetModalInView(null)}
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={modalInView === "change-insurance"}>
                <DialogContent
                    title="Change insurance provider"
                    variant="center"
                    handleCloseDialog={() => handleSetModalInView(null)}
                    showFooter
                    cancelText="Cancel"
                    saveText="Change insurance"
                    submitBtnFormValue="change-insurance"
                    isSubmitBtnDisabled={changeInsuranceIsMutating > 0}
                    isCancelBtnDisabled={changeInsuranceIsMutating > 0}
                >
                    <ChangeInsuranceForm
                        onFormSubmit={() => handleSetModalInView(null)}
                    />
                </DialogContent>
            </Dialog>
            <AlertDialog
                open={modalInView === "delete-flag" && Boolean(selectedFlagId)}
            >
                <AlertDialogContent
                    title="Are you sure you want to delete flag?"
                    description=" This will delete this flag from the client flags and
              cannot be reversed"
                    onCancelClick={() => handleSetModalInView(null)}
                    saveText="Delete flag"
                    onSaveClick={handleDeleteFlag}
                    isCancelBtnDisabled={deleteFlag.isLoading}
                    isSaveBtnDisabled={deleteFlag.isLoading}
                    isDeleting
                />
            </AlertDialog>
            <Dialog
                open={modalInView === "edit-flag" && Boolean(selectedFlagId)}
            >
                <DialogContent
                    title="Edit client flag"
                    handleCloseDialog={() => handleSetModalInView(null)}
                    variant="center"
                    saveText="Save changes"
                    submitBtnFormValue="edit-client-flag"
                    isCancelBtnDisabled={editFlagIsMutating > 0}
                    isSubmitBtnDisabled={editFlagIsMutating > 0}
                >
                    <EditClientFlagForm
                        onFormSubmit={() => handleSetModalInView(null)}
                        flagId={selectedFlagId || ""}
                    />
                </DialogContent>
            </Dialog>
            {notesLoading && <Loader wrapperClassName="h-[300px]" />}
            {noteSuccess && noteData?.data && (
                <Card classNames="p-24 h-auto uppercase flex flex-col h-[auto] max-[617px]:h-auto">
                    <div className="flex items-start justify-between gap-x-2 gap-y-3">
                        <div className="flex max-[673px]:w-full items-center gap-x-2 flex-wrap gap-y-3 w-full">
                            <div className="flex items-center gap-x-2">
                                <Avatar
                                    label={userInitials(
                                        noteData?.data?.client
                                            ?.first_name as string,
                                        noteData?.data?.client
                                            ?.last_name as string
                                    )}
                                    className="!w-[42px] !h-[42px] shrink-0 !text-text"
                                />
                                <p className="text-title font-semibold">
                                    {`${makeStringFirstLetterCapital(
                                        noteData?.data.client.first_name
                                    )}'s`}{" "}
                                    <span>
                                        {getNoteTypeTextWithTag(
                                            noteData?.data.type as NoteTypes
                                        )}
                                    </span>
                                </p>
                            </div>

                            <span>
                                {noteData.data.flagged_for_audit_status !==
                                    FlaggedNoteForAuditStatus.FLAGGED &&
                                noteData.data.flagged_for_audit_status !==
                                    FlaggedNoteForAuditStatus.REQUIRES_ACTION
                                    ? getNoteStatusTag(
                                          noteData.data.note_status
                                      )
                                    : null}
                            </span>
                            {viewFrom !== "session_note" &&
                                !isSingleNoteView &&
                                noteData.data?.invoice_status && (
                                    <>
                                        <Dropdown>
                                            <DropdownTrigger asChild>
                                                <Button
                                                    variant="normal"
                                                    size="auto"
                                                    className={cn(
                                                        "capitalize !text-caption px-2 h-5 !rounded-full flex items-center text-[--color-warning-text] bg-[--color-warning-bg] text-xs",
                                                        {
                                                            "bg-[--color-neutral-bg] text-[--color-neutral-text]":
                                                                [
                                                                    BillStatus.OPEN,
                                                                    BillStatus.CLOSED,
                                                                ].includes(
                                                                    noteData
                                                                        .data
                                                                        .invoice_status as BillStatus
                                                                ),
                                                            "bg-[--color-danger-bg] text-[--color-danger-text]":
                                                                [
                                                                    BillStatus.CARD_PAYMENT_DECLINED,
                                                                    BillStatus.CARD_PAYMENT_DECLINED_PR100,
                                                                ].includes(
                                                                    noteData
                                                                        .data
                                                                        .invoice_status as BillStatus
                                                                ),
                                                            "bg-[--color-info-bg] text-[--color-info-text]":
                                                                [
                                                                    BillStatus.UNCOLLECTABLE,
                                                                    BillStatus.CREDIT_BALANCE,
                                                                ].includes(
                                                                    noteData
                                                                        .data
                                                                        .invoice_status as BillStatus
                                                                ),
                                                            "disabled disabled:opacity-100 cursor-text":
                                                                !canMarkInvoice,
                                                        }
                                                    )}
                                                >
                                                    {`Bill: ${removeEnumUnderscore(
                                                        BillStatusLabels[
                                                            noteData.data
                                                                .invoice_status ||
                                                                ""
                                                        ]
                                                    )}`}
                                                    {canMarkInvoice && (
                                                        <ChevronDownIcon />
                                                    )}
                                                </Button>
                                            </DropdownTrigger>
                                            <DropdownContent
                                                width="auto"
                                                align="end"
                                            >
                                                {Object.values(BillStatus).map(
                                                    (status) => (
                                                        <DropdownItem
                                                            onClick={() =>
                                                                status ===
                                                                BillStatus.PENDING_RESOLUTION
                                                                    ? handleSetModalInView(
                                                                          "pending-resolution"
                                                                      )
                                                                    : handleChangeBillStatus(
                                                                          status
                                                                      )
                                                            }
                                                            key={status}
                                                            disabled={
                                                                patchInvoiceStatus.isLoading
                                                            }
                                                            hidden={
                                                                noteData.data
                                                                    .invoice_status ===
                                                                status
                                                            }
                                                        >
                                                            {
                                                                BillStatusLabels[
                                                                    status
                                                                ]
                                                            }
                                                        </DropdownItem>
                                                    )
                                                )}
                                            </DropdownContent>
                                        </Dropdown>

                                        {claimsInView ? (
                                            <Dropdown>
                                                <DropdownTrigger
                                                    asChild
                                                    disabled={
                                                        isStatusLockedByGroup
                                                    }
                                                >
                                                    <Button
                                                        variant="normal"
                                                        size="auto"
                                                        title={
                                                            isStatusLockedByGroup
                                                                ? "Status is managed on the grouped claim while this claim is grouped with others"
                                                                : undefined
                                                        }
                                                        className={cn(
                                                            `capitalize !text-caption px-2 h-5 !rounded-full flex items-center ${
                                                                getClaimStatusTagStylesForDropdown(
                                                                    claimsInView?.claim_status as ClaimStatus
                                                                ).className
                                                            }`,
                                                            {
                                                                "cursor-default":
                                                                    isStatusLockedByGroup,
                                                            }
                                                        )}
                                                    >
                                                        {`Claims: ${removeEnumUnderscore(
                                                            claimsInView?.claim_status ||
                                                                ""
                                                        )}`}
                                                        {!isStatusLockedByGroup && (
                                                            <ChevronDownIcon className="size-3.5" />
                                                        )}
                                                    </Button>
                                                </DropdownTrigger>

                                                <DropdownContent
                                                    width="auto"
                                                    align="end"
                                                    className="max-h-[250px] overflow-y-auto"
                                                >
                                                    {Object.values(ClaimStatus)
                                                        .filter(
                                                            (status) =>
                                                                status !==
                                                                claimsInView?.claim_status
                                                        )
                                                        .map((status) => (
                                                            <DropdownItem
                                                                key={status}
                                                                onClick={() => {
                                                                    handleChangeStatus(
                                                                        status
                                                                    );
                                                                }}
                                                            >
                                                                {makeStringFirstLetterCapital(
                                                                    removeEnumUnderscore(
                                                                        status
                                                                    )
                                                                )}
                                                            </DropdownItem>
                                                        ))}
                                                </DropdownContent>
                                            </Dropdown>
                                        ) : null}
                                        {remitId && remitData && (
                                            <span>
                                                {remitData?.data?.[0]
                                                    ?.remit_status ===
                                                RemitStatus.POSTED ? (
                                                    <Badge
                                                        content={`Remit: ${removeEnumUnderscore(
                                                            remitData?.data?.[0]
                                                                ?.remit_status ||
                                                                ""
                                                        )}`}
                                                        className="!text-caption px-2 h-5"
                                                        type="success"
                                                    />
                                                ) : (
                                                    <Badge
                                                        content={`Remit: ${removeEnumUnderscore(
                                                            remitData?.data?.[0]
                                                                ?.remit_status ||
                                                                ""
                                                        )}`}
                                                        className="!text-caption px-2 h-5"
                                                        type="neutral"
                                                    />
                                                )}
                                            </span>
                                        )}
                                    </>
                                )}
                            {claimsInView && claimsInView.is_voided && (
                                <Badge
                                    content="Voided Claim"
                                    type="danger"
                                    className="capitalize !text-caption px-2 h-5 !rounded-full flex items-center"
                                />
                            )}
                            {noteData?.data?.is_archived && (
                                <div className="ml-[10px]">
                                    {noteData.data.archive_notes ? (
                                        <Tooltip
                                            content={
                                                noteData.data.archive_notes
                                            }
                                        >
                                            <Button
                                                variant="normal"
                                                size="auto"
                                                className="font-normal px-8 py-4 h-6 text-xs !rounded-r4 bg-[#acdefa7f] text-[#165574]"
                                            >
                                                Archived Note
                                            </Button>
                                        </Tooltip>
                                    ) : (
                                        <Badge
                                            content="Archived Note"
                                            type="success"
                                        />
                                    )}
                                </div>
                            )}
                            {noteData?.data?.flagged_for_audit_status ===
                                FlaggedNoteForAuditStatus.FLAGGED ||
                            noteData?.data?.flagged_for_audit_status ===
                                FlaggedNoteForAuditStatus.REQUIRES_ACTION ? (
                                <span>
                                    {getFlaggedNoteStatusTag(
                                        noteData?.data?.flagged_for_audit_status
                                    )}
                                </span>
                            ) : null}
                        </div>

                        {isSingleNoteView || isSingleBillView ? (
                            <div className="shrink-0">
                                {isClientPinnedInLocation ? (
                                    <Tooltip content="Unpin Client">
                                        <Button
                                            onClick={() => {
                                                pinnedClientsContext?.handleUnpinClient(
                                                    clientId || "",
                                                    isSingleNoteView
                                                        ? "note"
                                                        : "bill"
                                                );
                                            }}
                                        >
                                            <UnpinIcon />
                                        </Button>
                                    </Tooltip>
                                ) : (
                                    <Tooltip content="Pin client">
                                        <Button
                                            onClick={() =>
                                                pinnedClientsContext?.handlePinClient(
                                                    {
                                                        clientId:
                                                            clientId || "",
                                                        clientName: `${noteData?.data?.client.first_name} ${noteData?.data?.client.last_name}`,
                                                        pinModule:
                                                            isSingleNoteView
                                                                ? "note"
                                                                : "bill",
                                                    }
                                                )
                                            }
                                        >
                                            <PinIcon />
                                        </Button>
                                    </Tooltip>
                                )}
                            </div>
                        ) : null}
                        {topRightAction}
                    </div>
                    <div className="pt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-normal">--</span>
                        {userCanViewTags && clientId ? (
                            <ClientTagBadges
                                clientId={clientId}
                                badgeClassName="!text-caption px-2 h-5"
                            />
                        ) : null}
                        {userCanEditTags && clientId ? (
                            <EditClientTagsMenu clientId={clientId}>
                                <Button
                                    variant="normal"
                                    size="auto"
                                    className="text-primary !text-caption font-semibold normal-case h-5 flex items-center"
                                >
                                    Edit profile tags
                                    <ChevronDownIcon
                                        className="ml-1"
                                        stroke="currentColor"
                                    />
                                </Button>
                            </EditClientTagsMenu>
                        ) : null}
                    </div>
                    <div className="pt-2 flex gap-x-2 gap-y-2 items-center flex-wrap">
                        {clientFlags.data &&
                            clientFlags.data.data.length > 0 &&
                            clientFlags.data.data.map((flag) => {
                                const parsedFlag = getClientFlagDetails(
                                    flag.content
                                );
                                return (
                                    <Dropdown key={flag.client_flag_id}>
                                        <DropdownTrigger asChild>
                                            <Button
                                                variant="normal"
                                                size="auto"
                                                className="capitalize !rounded-md px-8 py-4 h-6 text-xs"
                                                style={{
                                                    backgroundColor: parsedFlag
                                                        ? parsedFlag.color
                                                        : DEFAULT_FLAG_COLOR,
                                                    color: "#ffffff",
                                                }}
                                            >
                                                {parsedFlag
                                                    ? parsedFlag.label
                                                    : ""}
                                                <ChevronDownIcon
                                                    className="ml-1"
                                                    stroke="currentColor"
                                                />
                                            </Button>
                                        </DropdownTrigger>
                                        <DropdownContent
                                            width="auto"
                                            align="end"
                                        >
                                            <DropdownItem
                                                onClick={() => {
                                                    setSelectedFlagId(
                                                        flag.client_flag_id
                                                    );
                                                    setModalInView("edit-flag");
                                                }}
                                            >
                                                Edit
                                            </DropdownItem>
                                            <DropdownItem
                                                onClick={() => {
                                                    setSelectedFlagId(
                                                        flag.client_flag_id
                                                    );
                                                    setModalInView(
                                                        "delete-flag"
                                                    );
                                                }}
                                            >
                                                Delete
                                            </DropdownItem>
                                        </DropdownContent>
                                    </Dropdown>
                                );
                            })}
                    </div>
                    {!isSingleNoteView &&
                        clientMemos?.data &&
                        clientMemos?.data.count > 0 &&
                        clientMemos?.data?.data[0].description && (
                            <div className="mt-2 px-8 pt-[2px] rounded-md bg-[#d3f7fa66] min-h-[20px]">
                                <div className="flex items-center text-xs gap-x-1">
                                    <span className="text-primary font-bold shrink-0">
                                        Memo note:
                                    </span>
                                    <div className="!text-textSec *:!text-xs *:!font-normal">
                                        <ViewEditorText
                                            text={
                                                clientMemos?.data?.data[0]
                                                    .description || ""
                                            }
                                        />
                                    </div>
                                    <Link
                                        to={addPracticeParamToUrl(
                                            `/clients/${clientId}?tab=memo`,
                                            practice?.practice_id
                                        )}
                                        target="_blank"
                                        className="text-primary font-medium underline lowercase shrink-0"
                                    >
                                        view all memos...
                                    </Link>
                                </div>
                            </div>
                        )}
                    <Card classNames="[border:1px_solid_#fafafa)] border border-[#fafafa] bg-[--card-bg-gray] p-8 mt-1">
                        <div className="grid grid-cols-[repeat(2,minmax(0,1fr))] max-[900px]:grid-cols-1 gap-x-3 gap-y-2">
                            {/* Left Column */}
                            <div className="space-y-2">
                                <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                    <span className="text-xs font-medium text-textSec">
                                        PROVIDER
                                    </span>
                                    <span className="text-sm">
                                        {displayNameInRightFormat({
                                            firstName:
                                                noteData.data.provider
                                                    .first_name,
                                            lastName:
                                                noteData.data.provider
                                                    .last_name,
                                        })}
                                    </span>
                                </div>
                                <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                    <span className="text-xs font-medium text-textSec">
                                        CLIENT NAME
                                    </span>
                                    <Link
                                        to={`/clients/${noteData.data.client.client_id}`}
                                        className="text-sm underline text-textLink"
                                    >
                                        {displayNameInRightFormat({
                                            firstName:
                                                noteData.data.client.first_name,
                                            lastName:
                                                noteData.data.client.last_name,
                                        })}
                                    </Link>
                                </div>
                                <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                    <span className="text-xs font-medium text-textSec">
                                        APPOINTMENT
                                    </span>
                                    <span className="text-sm">
                                        {makeStringFirstLetterCapital(
                                            removeEnumUnderscore(
                                                noteData.data.appointment_type
                                            )
                                        )}
                                    </span>
                                </div>
                                {viewFrom !== "session_note" ? (
                                    <>
                                        {" "}
                                        <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                            <span className="text-xs font-medium text-textSec">
                                                MANTLE CLAIM ID
                                            </span>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm">
                                                    {mantleClaimIdToDisplay}
                                                </span>
                                                {claimsInView &&
                                                    claimsInView.is_voided && (
                                                        <Badge
                                                            content="Voided Claim"
                                                            type="danger"
                                                        />
                                                    )}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                            <span className="text-xs font-medium text-textSec">
                                                PAYER CLAIM ID
                                            </span>
                                            <span className="text-sm">
                                                {claimsInView?.payer_claim_id ||
                                                    "--"}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                            <span className="text-xs font-medium text-textSec">
                                                SUBMISSION
                                            </span>
                                            <span className="text-sm">
                                                {claimsInView?.submitted_on
                                                    ? formatDate(
                                                          claimsInView.submitted_on,
                                                          false,
                                                          "MM/dd/yyyy HH:mmaa"
                                                      )
                                                    : "--"}
                                            </span>
                                        </div>
                                    </>
                                ) : null}
                                {noteData.data.type !==
                                    NoteTypes.CANCELLATION_NOTE &&
                                    noteData.data.type !==
                                        NoteTypes.SUPERVISION_NOTE && (
                                        <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                            <span className="text-xs font-medium text-textSec">
                                                POS CODE
                                            </span>
                                            <div className="flex items-center gap-x-1">
                                                <span className="text-sm">
                                                    {noteData?.data.pos_code ||
                                                        "--"}
                                                </span>
                                                {canEditCptAndModifiersCode && (
                                                    <Button
                                                        size="auto"
                                                        variant="normal"
                                                        onClick={
                                                            handleCodeModalToShow
                                                        }
                                                    >
                                                        <EditIcon className="stroke-textLink" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                    <span className="text-xs font-medium text-textSec">
                                        DOB
                                    </span>
                                    <span className="text-sm">
                                        {noteData?.data.client.date_of_birth
                                            ? format(
                                                  convertDateFilterStringToDate(
                                                      noteData?.data.client
                                                          .date_of_birth
                                                  ) as Date,
                                                  "MM/dd/yyyy"
                                              )
                                            : "--"}
                                        {noteData?.data.client.age
                                            ? ` (${noteData?.data.client.age} Yrs)`
                                            : ""}
                                    </span>
                                </div>
                                {isUserViewingNoteWithoutABill ? (
                                    <>
                                        <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center overflow-x-auto">
                                            <span className="text-xs font-medium text-textSec">
                                                EMAIL
                                            </span>
                                            <span className="text-sm">
                                                {noteData.data.client.email
                                                    ? handleDisplayClientEmailCorrectly(
                                                          noteData.data.client
                                                              .email
                                                      )
                                                    : "--"}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                            <span className="text-xs font-medium text-textSec">
                                                SEX
                                            </span>
                                            <span className="text-sm">
                                                {noteData.data.client.sex ||
                                                    "--"}
                                            </span>
                                        </div>
                                    </>
                                ) : null}
                            </div>

                            {/* Right Column */}
                            <div className="space-y-2">
                                <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                    <span className="text-xs font-medium text-textSec">
                                        INSURANCE
                                    </span>

                                    <div className="text-xs font-semibold">
                                        <div className="flex items-center gap-x-1">
                                            {insuranceProviderIsLoading ? (
                                                <Loader loaderType="inline" />
                                            ) : (
                                                <>
                                                    <p className="text-sm font-medium">
                                                        {insuranceDetailsToShow()}
                                                    </p>

                                                    {canChangeInsurance && (
                                                        <Button
                                                            size="auto"
                                                            variant="normal"
                                                            onClick={() =>
                                                                handleSetModalInView(
                                                                    "change-insurance"
                                                                )
                                                            }
                                                        >
                                                            <EditIcon className="stroke-textLink" />
                                                        </Button>
                                                    )}

                                                    {clientData?.data
                                                        .financial_assistance_status ===
                                                        FinancialAssistanceStatus.YES && (
                                                        <Badge
                                                            content="FAP"
                                                            type="info"
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                    <span className="text-xs font-medium text-textSec">
                                        MEMBER ID
                                    </span>
                                    <span className="text-sm">
                                        {claimsInView?.claim_content
                                            ?.subscriberId || "--"}
                                    </span>
                                </div>
                                <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                    <span className="text-xs font-medium text-textSec">
                                        CHART NUMBER
                                    </span>
                                    <span className="text-sm">
                                        {clientData?.data.client_chart_number ||
                                            "--"}
                                    </span>
                                </div>
                                <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                    <span className="text-xs font-medium text-textSec">
                                        {primaryCptLabel}
                                    </span>
                                    <div className="flex items-center gap-x-1">
                                        <span className="text-sm">
                                            {getCptCodeToDisplay({
                                                noteType: noteData.data.type,
                                                cptCode: noteData.data.cpt_code,
                                                unitCalculation:
                                                    noteData?.data
                                                        ?.note_metadata
                                                        ?.unit_calculation,
                                            })}
                                        </span>
                                        {canEditCptAndModifiersCode &&
                                        noteData.data.type !==
                                            NoteTypes.CANCELLATION_NOTE ? (
                                            <Button
                                                size="auto"
                                                variant="normal"
                                                onClick={handleCodeModalToShow}
                                            >
                                                <EditIcon className="stroke-textLink" />
                                            </Button>
                                        ) : null}
                                    </div>
                                </div>
                                {additionalCptLabel ? (
                                    <div
                                        className={cn(
                                            "grid grid-cols-[140px,1fr]",
                                            "gap-x-1 items-center"
                                        )}
                                    >
                                        <span className="text-xs font-medium text-textSec">
                                            {additionalCptLabel}
                                        </span>
                                        <span className="text-sm">
                                            {noteData.data
                                                .additional_cpt_codes?.[0] ??
                                                "--"}
                                        </span>
                                    </div>
                                ) : null}
                                <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                    <span className="text-xs font-medium text-textSec">
                                        DATE OF SERVICE
                                    </span>
                                    <div className="flex items-center gap-x-2">
                                        <span className="text-sm">
                                            {formatDate(
                                                noteData.data.date_of_service
                                            )}{" "}
                                            {formatDate(
                                                noteData.data
                                                    .session_start_time as string,
                                                false,
                                                "h:mm a"
                                            )}{" "}
                                            -{" "}
                                            {formatDate(
                                                noteData.data
                                                    .session_end_time as string,
                                                false,
                                                "h:mm a"
                                            )}
                                        </span>
                                        <span className="text-sm font-medium">
                                            (
                                            {getTimeDuration(
                                                formatDate(
                                                    noteData.data
                                                        .session_start_time ||
                                                        "",
                                                    false,
                                                    "HH:mm"
                                                ),
                                                formatDate(
                                                    noteData.data
                                                        .session_end_time || "",
                                                    false,
                                                    "HH:mm"
                                                )
                                            )}
                                            )
                                        </span>
                                        {noteData.data
                                            .reason_for_short_duration && (
                                            <Tooltip
                                                content={`Reason for short session: ${noteData.data.reason_for_short_duration}`}
                                            >
                                                <Button
                                                    variant="normal"
                                                    size="auto"
                                                >
                                                    <InfoIcon />
                                                </Button>
                                            </Tooltip>
                                        )}
                                        {canEditNoteSessionTime && (
                                            <Button
                                                size="auto"
                                                variant="normal"
                                                onClick={() =>
                                                    handleSetModalInView(
                                                        "session-time"
                                                    )
                                                }
                                            >
                                                <EditIcon className="stroke-textLink" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                {isUserViewingNoteWithoutABill ? (
                                    <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                        <span className="text-xs font-medium text-textSec">
                                            CONTACT
                                        </span>
                                        <span className="text-sm">
                                            {noteData?.data.client.contact ||
                                                "--"}
                                        </span>
                                    </div>
                                ) : null}
                                <div className="grid grid-cols-[140px,1fr] gap-x-1 items-center">
                                    <span className="text-xs font-medium text-textSec">
                                        SIGNATURE
                                    </span>
                                    <span className="text-sm">
                                        {noteData.data.note_signatures?.find(
                                            (item) => item.role === "author"
                                        )?.signature || "--"}
                                        {noteData.data.note_signatures?.length >
                                            0 &&
                                            ` (${formatDate(
                                                noteData.data.note_signatures.find(
                                                    (item) =>
                                                        item.role === "author"
                                                )?.signature_date_time as string
                                            )}, ${formatDate(
                                                noteData.data.note_signatures.find(
                                                    (item) =>
                                                        item.role === "author"
                                                )
                                                    ?.signature_date_time as string,
                                                false,
                                                "h:mm a"
                                            )})`}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Modifier and Diagnosis Section */}
                        {noteData?.data.type !== NoteTypes.CANCELLATION_NOTE &&
                            noteData?.data.type !==
                                NoteTypes.SUPERVISION_NOTE && (
                                <div className="mt-4 border-t border-[#E5E5E5] pt-4">
                                    <div className="grid grid-cols-2 gap-x-6 flex-1 max-[600px]:grid-cols-1 gap-y-4">
                                        <Card classNames="bg-[--bg-encounter] min-h-[90px] relative pl-0 pt-0 pb-12 overflow-auto pr-[6px]">
                                            <div className="sticky h-[28px] w-[2px] left-0 top-2/4 -translate-y-2/4 bg-[#BC2C7C]" />
                                            <div className="pl-12 pt-12 -mt-[28px]">
                                                <div className="text-xs font-semibold capitalize">
                                                    <div className="flex items-center justify-between gap-x-1">
                                                        <p className="text-textSec font-medium text-sm">
                                                            MODIFIER
                                                        </p>
                                                        {canEditCptAndModifiersCode ? (
                                                            <Button
                                                                size="auto"
                                                                variant="normal"
                                                                className="underline text-textLink mr-2"
                                                                onClick={
                                                                    handleCodeModalToShow
                                                                }
                                                            >
                                                                Edit
                                                            </Button>
                                                        ) : null}
                                                    </div>
                                                    <div className="font-medium text-sm flex flex-col pt-4 gap-y-[2px]">
                                                        {noteData?.data
                                                            ?.modifier_codes &&
                                                        noteData.data
                                                            .modifier_codes
                                                            .length > 0 ? (
                                                            getModifierCodes()?.map(
                                                                (code) => (
                                                                    <div
                                                                        key={
                                                                            code
                                                                        }
                                                                        className="text-xs"
                                                                    >
                                                                        <span className="text-textSec">
                                                                            {
                                                                                code
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                )
                                                            )
                                                        ) : (
                                                            <span className="text-sm">
                                                                --
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                        <Card classNames="bg-[--bg-billed] min-h-[90px] relative pl-0 pt-0 pb-12 overflow-auto pr-[6px]">
                                            <div className="sticky h-[28px] w-[2px] left-0 top-2/4 -translate-y-2/4 bg-[#1A738D]" />
                                            <div className="pl-12 pt-12 -mt-[28px]">
                                                <div className="text-xs font-semibold capitalize">
                                                    <div className="flex items-center justify-between gap-x-1">
                                                        <p className="text-textSec font-medium text-sm">
                                                            DIAGNOSIS
                                                        </p>
                                                        {canEditDiagnosisCodes && (
                                                            <Button
                                                                size="auto"
                                                                variant="normal"
                                                                className="underline text-textLink mr-2"
                                                                onClick={() => {
                                                                    if (
                                                                        noteContent
                                                                    ) {
                                                                        handleSetModalInView(
                                                                            "diagnosis-codes"
                                                                        );
                                                                    } else {
                                                                        handleSetModalInView(
                                                                            "edit-manual-claim"
                                                                        );
                                                                    }
                                                                }}
                                                            >
                                                                Edit
                                                            </Button>
                                                        )}
                                                    </div>
                                                    <div className="font-medium text-sm flex flex-col pt-4 gap-y-[2px]">
                                                        {diagnosisCodes.isLoading && (
                                                            <Loader loaderType="inline" />
                                                        )}
                                                        {diagnosisCodes.isError && (
                                                            <span className="block text-sm font-medium text-danger">
                                                                Error fetching
                                                                diagnosis codes
                                                            </span>
                                                        )}
                                                        {diagnosisCodes.isSuccess &&
                                                            diagnosisCodes.data
                                                                .data && (
                                                                <div className="text-xs space-y-1">
                                                                    <div>
                                                                        <span className="text-textSec">
                                                                            Primary:{" "}
                                                                        </span>
                                                                        <span className="text-textSec">
                                                                            {noteData
                                                                                ?.data
                                                                                .source ===
                                                                            Source.MANUAL
                                                                                ? primaryDiagnosisManual
                                                                                : getDiagnosisCodes()?.map(
                                                                                      (
                                                                                          code,
                                                                                          i
                                                                                      ) => (
                                                                                          <span
                                                                                              key={
                                                                                                  code.code
                                                                                              }
                                                                                          >
                                                                                              {`${code.code} ${code.description}`}
                                                                                              {i !==
                                                                                              2
                                                                                                  ? ", "
                                                                                                  : ""}
                                                                                          </span>
                                                                                      )
                                                                                  ) ||
                                                                                  "--"}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-textSec">
                                                                            Secondary:{" "}
                                                                        </span>
                                                                        <span className="text-textSec">
                                                                            {
                                                                                secondaryDiagnosis
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-textSec">
                                                                            Tertiary:{" "}
                                                                        </span>
                                                                        <span className="text-textSec">
                                                                            {
                                                                                tertiaryDiagnosis
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-textSec">
                                                                            Additional
                                                                            Diagnosis
                                                                            1:{" "}
                                                                        </span>
                                                                        <span className="text-textSec">
                                                                            {
                                                                                additionalDiagnosisOne
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-textSec">
                                                                            Additional
                                                                            Diagnosis
                                                                            2:{" "}
                                                                        </span>
                                                                        <span className="text-textSec">
                                                                            {
                                                                                additionalDiagnosisTwo
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </div>
                                </div>
                            )}
                    </Card>
                    {noteData?.data?.is_flagged_for_audit &&
                        noteData?.data?.flagged_for_audit_notes && (
                            <div className="mt-24">
                                <Alert
                                    type="info"
                                    title="Reason for flagging note"
                                    description={
                                        noteData?.data?.flagged_for_audit_notes
                                    }
                                />
                            </div>
                        )}
                </Card>
            )}
        </div>
    );
}
